import { GetServerSideProps, NextPage } from 'next'
import nextI18nextConfig from '~/next-i18next.config'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { DependencyInjector } from '~/injector/dependency.injector'
import { HtmlPageMetaContextProps } from '~/components/HtmlPageMeta/HtmlPageMetaContextProps'
import { HtmlPageMetaContextService } from '~/components/HtmlPageMeta/HtmlPageMetaContextService'
import { TrophyRepositoryInterface } from '~/modules/Trophies/Domain/TrophyRepositoryInterface'
import { GetTrophies } from '~/modules/Trophies/Application/GetTrophies'
import { OrganizerRepositoryInterface } from '~/modules/Organizers/Domain/OrganizerRepositoryInterface'
import { GetAllOrganizers } from '~/modules/Organizers/Application/GetAllOrganizers/GetAllOrganizers'
import { OrganizersApplicationDto } from '~/modules/Organizers/Application/GetAllOrganizers/OrganizersApplicationDto'
import { ApplicationTrophyDto } from '~/modules/Products/Application/Dtos/ApplicationTrophyDto'
import { MarketTrophiesPage } from '~/components/MarketPages/TrophiesPage'
import { fromTrophiesPagePropsToViewModel } from '~/modules/PresentationAdapters/TrophiesPageAdapter'
import {
  DEFAULT_TROPHY_MEMORIES_FILTER,
  parseTrophyDisciplineFilter,
  parseTrophyMemoriesFilter,
  parseTrophyPositionFilter,
  TrophyDisciplineFilterOption,
  TrophyMemoriesFilter,
} from '~/modules/Trophies/Application/TrophyListFilters'

export interface TrophiesPageProps {
  trophies: ApplicationTrophyDto[]
  organizers: OrganizersApplicationDto[]
  organizerId: string | null
  minMemories: TrophyMemoriesFilter
  disciplineOptions?: TrophyDisciplineFilterOption[]
  discipline?: string | null
  positionOptions?: string[]
  position?: string | null
  htmlPageMetaContextProps: HtmlPageMetaContextProps
}

function getSingleQueryParam (value: string | string[] | undefined): string | undefined {
  if (Array.isArray(value)) {
    return value[0]
  }

  return value
}

function getTrophiesDestination (
  locale: string,
  organizerId: string | null,
  minMemories: TrophyMemoriesFilter = DEFAULT_TROPHY_MEMORIES_FILTER,
  discipline: string | null = null,
  position: string | null = null
): string {
  const params = new URLSearchParams()

  if (organizerId !== null) {
    params.append('organizerId', organizerId)
  }

  if (minMemories > DEFAULT_TROPHY_MEMORIES_FILTER) {
    params.append('minMemories', minMemories.toString())
  }

  if (discipline !== null) {
    params.append('discipline', discipline)
  }

  if (position !== null) {
    params.append('position', position)
  }

  const queryString = params.toString()

  return queryString.length > 0
    ? `/${locale}/trophies?${queryString}`
    : `/${locale}/trophies`
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const locale = context.locale ? context.locale : nextI18nextConfig.i18n.defaultLocale
  const translationsPromise = serverSideTranslations(locale, [
    'user_tos',
    'app_menu',
    'filters',
    'footer',
    'gallery',
    'trophies',
    'wallet_error_modal',
  ])

  let organizerId: string | null = null
  const rawMinMemories = getSingleQueryParam(context.query?.minMemories)
  const minMemories = parseTrophyMemoriesFilter(rawMinMemories)
  const discipline = parseTrophyDisciplineFilter(getSingleQueryParam(context.query?.discipline))
  const position = parseTrophyPositionFilter(getSingleQueryParam(context.query?.position))

  if (context.query !== undefined && context.query.organizerId !== undefined) {
    organizerId = getSingleQueryParam(context.query.organizerId) ?? null
  }

  if (minMemories === null) {
    return {
      redirect: {
        permanent: false,
        destination: getTrophiesDestination(
          locale,
          organizerId,
          DEFAULT_TROPHY_MEMORIES_FILTER,
          discipline,
          position
        ),
      },
    }
  }

  const organizerRepository: OrganizerRepositoryInterface =
    DependencyInjector.getInstance().resolve('OrganizerRepositoryInterface')
  const trophyRepository: TrophyRepositoryInterface =
    DependencyInjector.getInstance().resolve('TrophyRepositoryInterface')

  const getTrophiesUseCase = new GetTrophies(trophyRepository)
  const getOrganizersUseCase = new GetAllOrganizers(organizerRepository)
  let trophies: ApplicationTrophyDto[] = []
  let organizers: OrganizersApplicationDto[] = []
  let disciplineOptions: TrophyDisciplineFilterOption[] = []
  let positionOptions: string[] = []

  try {
    const filters = {
      organizerId,
      minMemories: minMemories ?? DEFAULT_TROPHY_MEMORIES_FILTER,
      ...(discipline !== null && { discipline }),
      ...(position !== null && { position }),
    }
    trophies = await getTrophiesUseCase.run(1, 12, filters)
  } catch (exception: unknown) {
    console.error('Failed to load trophies for trophies page', exception)
  }

  try {
    organizers = await getOrganizersUseCase.get()
  } catch (exception: unknown) {
    console.error('Failed to load organizers for trophies page', exception)
  }

  try {
    disciplineOptions = await trophyRepository.findDisciplines()
  } catch (exception: unknown) {
    console.error('Failed to load trophy disciplines for trophies page', exception)
  }

  try {
    positionOptions = await trophyRepository.findPositions()
  } catch (exception: unknown) {
    console.error('Failed to load trophy positions for trophies page', exception)
  }

  if (organizerId !== null && organizers.length > 0) {
    const hasOrganizer = organizers.find(
      (organizer) => organizer.id === organizerId
    )

    if (hasOrganizer === undefined) {
      return {
        redirect: {
          permanent: false,
          destination: `/${locale}/gallery`,
        },
      }
    }
  }

  const htmlPageMetaContextService = new HtmlPageMetaContextService(context)

  return {
      props: {
        trophies: JSON.parse(JSON.stringify(trophies)),
        organizers: JSON.parse(JSON.stringify(organizers)),
        organizerId,
        minMemories: minMemories ?? DEFAULT_TROPHY_MEMORIES_FILTER,
        disciplineOptions,
        discipline,
        positionOptions,
        position,
        htmlPageMetaContextProps: htmlPageMetaContextService.getProperties(),
        ...await translationsPromise,
      },
  }
}

const TrophiesRoutePage: NextPage<TrophiesPageProps> = (props) => {
  return <MarketTrophiesPage viewModel={ fromTrophiesPagePropsToViewModel(props) } />
}

export default TrophiesRoutePage
